import { resolveNativeReportGroups, hasNativeReportGroup } from './reportGroupAccess';
import {
  Briefcase,
  TrendingUp,
  List,
  Users,
  Star,
  BarChart3,
  Telescope,
  Moon,
  Radar,
  ShieldAlert,
} from 'lucide-react';

/**
 * Native report catalog — the same 10 pages the sidebar lists, with icons
 * and one-line blurbs for the mobile-style catalog page and tab bar. Paths
 * match App.js routes exactly.
 */
export const NATIVE_REPORTS_CATALOG = [
  {
    group: 'Financial',
    pages: [
      { key: 'financialPnl', title: 'Profit & Loss', path: '/reports/financial/pnl', icon: Briefcase, blurb: 'Net income vs budget, trends, income statement' },
      { key: 'financialTrends', title: 'Financial Trends', path: '/reports/financial/trends', icon: TrendingUp, blurb: 'Revenue, expense and EBITDA over time' },
      { key: 'glDetail', title: 'GL Detail', path: '/reports/financial/gl-detail', icon: List, blurb: 'Transaction ledger with debits and credits' },
    ],
  },
  {
    group: 'Operations',
    pages: [
      { key: 'laborManagement', title: 'Labor Management', path: '/reports/operations/labor', icon: Users, blurb: 'Payroll %, hours vs budget, overtime' },
      { key: 'reviews', title: 'Guest Reviews', path: '/reports/operations/reviews', icon: Star, blurb: 'Ratings by source and the review feed' },
      { key: 'nightAuditReview', title: 'Night Audit Review', path: '/reports/operations/night-audit', icon: ShieldAlert, blurb: 'Ledger, fee, tax and report exceptions found in the nightly audit files', flag: 'night-audit-review' },
    ],
  },
  {
    group: 'Revenue',
    pages: [
      // Overview + Trends collapsed into one workspace with in-page tabs
      // (Overview / Trend / Detail) — old /trends links land on the Trend tab.
      { key: 'revenueOverview', title: 'Revenue', path: '/reports/revenue/overview', icon: BarChart3, blurb: 'Overview, trends and property detail in one workspace' },
      { key: 'revenueForecast', title: 'Revenue Forecast', path: '/reports/revenue/forecast', icon: Telescope, blurb: 'Actual + on-the-books revenue ahead' },
      { key: 'nightAudits', title: 'Night Audits', path: '/reports/revenue/night-audits', icon: Moon, blurb: 'Imported audit and occupancy files' },
      { key: 'marketPerformance', title: 'Market Performance', path: '/reports/revenue/market-performance', icon: Radar, blurb: 'STR benchmark indices, comp set share and day by day detail from your STAR reports' },
    ],
  },
];

/**
 * Filter the catalog by the user's native report groups
 * (orgAccess.nativeReports — their PBI report grants mapped server-side).
 * null/undefined = unrestricted; [] = no reports; array = only those groups.
 * Pages with a `flag` show only when `flags[flag] === true`
 * (useReportCatalogFlags) — callers that pass no flags never list them.
 */
export function filterCatalogByReportGroups(catalog, nativeGroups, flags = {}) {
  const groups = Array.isArray(nativeGroups)
    ? (catalog || []).filter((g) => nativeGroups.includes(String(g.group || '').toLowerCase()))
    : (catalog || []);
  return groups.map((g) => (
    g.pages.some((p) => p.flag) ? { ...g, pages: g.pages.filter((p) => !p.flag || flags[p.flag] === true) } : g
  ));
}

// Re-exported so existing catalog importers keep one place to reach for
// report-group access; the implementations live in an icon-free module.
export { resolveNativeReportGroups, hasNativeReportGroup };

export default NATIVE_REPORTS_CATALOG;
